/**
 * The Glee AI mark, drawn rather than fetched.
 *
 * Vector for three reasons that all bite in this product specifically. The
 * sidebar renders it at 28px and the login page at 200px, and a raster asset
 * is soft at one of those. The portal is frequently installed on a private
 * network, so it must not depend on anything being reachable. And it appears
 * on the login page — before any request has been made — so a version that
 * arrives over HTTP shows a gap first.
 *
 * An operator who uploads their own logo gets theirs instead: see
 * `useBranding`, which prefers `logo_url` and falls back here. This is the
 * default, not the only option.
 *
 * The gradient IDs are suffixed per instance. Two of these on one page — the
 * sidebar and a preview on the branding screen — would otherwise both
 * reference the same `<defs>`, and whichever unmounted first would take the
 * gradient with it, leaving the other one black.
 */

import { useId } from 'react';

interface MarkProps {
  className?: string;
  /** Pixel size of the square mark. */
  size?: number;
  /**
   * Render in one flat colour instead of the gradient — for a dark sidebar,
   * where a blue-to-violet mark on navy reads as a smudge.
   */
  mono?: boolean;
}

/**
 * The badge on its own: a speech-bubble ring around a robot's face.
 *
 * Used wherever there is no room for the wordmark — the sidebar, the mobile
 * header, the favicon.
 */
export function GleeMark({ className, size = 28, mono = false }: MarkProps) {
  const id = useId();
  const ring = mono ? 'currentColor' : `url(#${id}-ring)`;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      className={className}
      role="img"
      aria-label="Glee AI"
    >
      <defs>
        <linearGradient id={`${id}-ring`} x1="6" y1="8" x2="56" y2="58">
          <stop offset="0%" stopColor="#2f7cf6" />
          <stop offset="55%" stopColor="#3b5bf6" />
          <stop offset="100%" stopColor="#8b3df0" />
        </linearGradient>
      </defs>

      {/* The ring, opened at the right so it reads as a speech bubble rather
          than a circle, with the tail dropping from the bottom. */}
      <path
        d="M45.5 9.5A26 26 0 1 0 52 43.6l.2 8.9a2.4 2.4 0 0 1-3.9 1.9l-8.6-6.6"
        stroke={ring}
        strokeWidth="7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* The face. A single dark slab so it holds together at 20px, where
          separate features turn to mush. */}
      <rect x="18" y="21" width="28" height="22" rx="9" fill="#0f1c3f" />

      {/* Two happy arcs. Drawn as strokes, not filled shapes: at small sizes a
          filled crescent closes up and the face stops smiling. */}
      <path
        d="M25 32.5c1-2 3.2-2 4.2 0M34.8 32.5c1-2 3.2-2 4.2 0"
        stroke="#38bdf8"
        strokeWidth="2.6"
        strokeLinecap="round"
      />

      {/* Headset: earpiece and boom mic. What says "this answers phones". */}
      <rect x="13.5" y="27" width="5" height="10" rx="2.5" fill={ring} />
      <rect x="45.5" y="27" width="5" height="10" rx="2.5" fill={ring} />
      <path
        d="M16 37v5.5a4 4 0 0 0 4 4h4.5"
        stroke={ring}
        strokeWidth="2.4"
        strokeLinecap="round"
      />
      <circle cx="26" cy="46.5" r="3" fill={mono ? 'currentColor' : '#38bdf8'} />
    </svg>
  );
}

interface LogoProps {
  className?: string;
  /** Height of the lockup in pixels; the width follows. */
  height?: number;
  /** Product name, so an operator's own name appears in their own logo. */
  name?: string;
  tagline?: string | null;
  /** For dark backgrounds: the wordmark goes white instead of near-black. */
  light?: boolean;
}

/**
 * The full lockup: mark, wordmark, waveform, and the tagline underneath.
 *
 * For the login page and the branding preview, where there is room for it.
 * The wordmark splits at "Glee"/"AI" so the second half can carry the
 * gradient, which is what makes it read as one piece with the mark.
 */
export function GleeLogo({
  className,
  height = 44,
  name = 'Glee AI',
  tagline,
  light = false,
}: LogoProps) {
  const id = useId();
  // "GleeAI" -> "Glee" + "AI". A custom name with no obvious split is drawn
  // whole in the solid colour rather than cut at an arbitrary letter.
  const match = /^(.*?)\s*(AI)$/i.exec(name.trim());
  const head = match ? match[1] : name;
  const tail = match ? match[2] : '';

  return (
    <div className={className}>
      <div className="flex items-center gap-3">
        <GleeMark size={height} />
        <svg
          height={height * 0.62}
          viewBox="0 0 260 40"
          fill="none"
          role="img"
          aria-label={name}
          className="overflow-visible"
        >
          <defs>
            <linearGradient id={`${id}-word`} x1="0" y1="0" x2="260" y2="40">
              <stop offset="0%" stopColor="#2f7cf6" />
              <stop offset="100%" stopColor="#8b3df0" />
            </linearGradient>
          </defs>
          <text
            x="0"
            y="31"
            fontSize="34"
            fontWeight="700"
            letterSpacing="-1.2"
            fill={light ? '#ffffff' : '#0f1c3f'}
            fontFamily="ui-sans-serif, system-ui, -apple-system, sans-serif"
          >
            {head}
            {tail && <tspan fill={`url(#${id}-word)`}>{tail}</tspan>}
          </text>

          {/* The waveform. Four bars, tallest in the middle — a sound level
              rather than a chart, which is the thing being suggested. */}
          {[
            { x: 0, h: 12 },
            { x: 11, h: 26 },
            { x: 22, h: 18 },
            { x: 33, h: 8 },
          ].map((bar, index) => (
            <rect
              key={index}
              x={(tail ? head.length * 19 + 46 : head.length * 19 + 12) + bar.x}
              y={20 - bar.h / 2}
              width="5"
              height={bar.h}
              rx="2.5"
              fill={`url(#${id}-word)`}
            />
          ))}
        </svg>
      </div>
      {tagline && (
        <p
          className={`mt-2 text-center text-sm ${
            light ? 'text-white/70' : 'text-ink-500 dark:text-ink-400'
          }`}
        >
          {tagline}
        </p>
      )}
    </div>
  );
}
